import React from 'react';
import cn from 'classnames';
import type { Country } from 'react-phone-number-input';
import {
  getCountries,
  getCountryCallingCode
} from 'react-phone-number-input/input';
import { parsePhoneNumber } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';

import ArrowDownIcon from 'widget/shared/icons/ArrowDownIcon';
import { IconColor, IconSize } from 'widget/shared/icons/BaseIcon';
import { Dropdown } from 'widget/shared/ui/Dropdown';
import type { DropdownOption } from 'widget/shared/ui/Dropdown';

import styles from './PhoneInput.module.scss';

export type PhoneInputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  disabled?: boolean;
  defaultCountry?: Country;
  error?: boolean;
  hasSuffix?: boolean;
  className?: string;
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  disabled = false,
  defaultCountry = 'RU',
  error = false,
  hasSuffix = false,
  className
}) => {
  const [selectedCountry, setSelectedCountry] = React.useState<Country>(() => {
    if (value) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed?.country) {
          return parsed.country;
        }
      } catch {
        return defaultCountry;
      }
    }
    return defaultCountry;
  });

  const [nationalDigits, setNationalDigits] = React.useState<string>(() => {
    if (value) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed) {
          return parsed.nationalNumber || '';
        }
      } catch {
        return '';
      }
    }
    return '';
  });

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Флаг: изменение инициировано внутри компонента (набор текста / выбор страны)
  const isInternalChangeRef = React.useRef(false);

  // Синхронизация только для внешних изменений (сброс формы и т.п.)
  React.useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    if (!value) {
      setSelectedCountry(defaultCountry);
      setNationalDigits('');
      return;
    }

    try {
      const parsed = parsePhoneNumber(value);
      if (parsed?.country) {
        setSelectedCountry(parsed.country);
      }
      setNationalDigits(parsed?.nationalNumber ?? '');
    } catch {
      return; // Не удалось распарсить — оставляем текущее состояние
    }
  }, [value, defaultCountry]);

  const callingCode = React.useMemo(
    () => getCountryCallingCode(selectedCountry),
    [selectedCountry]
  );

  const countryOptions: DropdownOption[] = React.useMemo(() => {
    return getCountries().map((country) => ({
      value: country,
      label: en[country] || country,
      secondaryLabel: `+${getCountryCallingCode(country)}`
    }));
  }, []);

  const toggleDropdown = React.useCallback(() => {
    if (disabled) {
      return;
    }
    setIsDropdownOpen((prev) => !prev);
  }, [disabled]);

  const closeDropdown = React.useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const handleCountrySelect = React.useCallback(
    (countryValue: string) => {
      const country = countryValue as Country;
      setSelectedCountry(country);
      setIsDropdownOpen(false);

      // При смене страны очищаем номер — формат номера различается
      setNationalDigits('');
      isInternalChangeRef.current = true;
      onChange('');
    },
    [onChange]
  );

  // E.164: max 15 digits total (including country code)
  const maxNationalDigits = Math.max(15 - String(callingCode).length, 4);

  const handlePhoneChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const digits = event.target.value
        .replace(/\D/g, '')
        .slice(0, maxNationalDigits);

      if (digits === nationalDigits) {
        return;
      }

      setNationalDigits(digits);
      isInternalChangeRef.current = true;
      onChange(digits ? `+${callingCode}${digits}` : '');
    },
    [callingCode, maxNationalDigits, nationalDigits, onChange]
  );

  return (
    <div
      className={cn(
        styles['phone-input'],
        error && styles['phone-input_error'],
        className
      )}
    >
      <div
        ref={wrapperRef}
        className={cn(
          styles['phone-input__country-wrapper'],
          disabled && styles['phone-input__country-wrapper_disabled']
        )}
        onClick={toggleDropdown}
      >
        <span className={styles['phone-input__country-label']}>
          {selectedCountry}
        </span>
        <ArrowDownIcon
          size={IconSize.XXS}
          color={IconColor.Gray}
          className={cn(
            styles['phone-input__arrow'],
            isDropdownOpen && styles['phone-input__arrow_open']
          )}
        />
      </div>
      <Dropdown
        options={countryOptions}
        value={selectedCountry}
        isOpen={isDropdownOpen}
        onClose={closeDropdown}
        onSelect={handleCountrySelect}
        triggerRef={wrapperRef}
      />
      <span className={styles['phone-input__code']}>+{callingCode}</span>
      <input
        type="tel"
        className={cn(
          styles['phone-input__number'],
          hasSuffix && styles['phone-input__number_withSuffix']
        )}
        value={nationalDigits}
        onChange={handlePhoneChange}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

export default React.memo(PhoneInput);
